import { z } from "zod";
import { csv, dateStr, flag, id, instantDateTime, limit, localDateTime, nullableText } from "./common";
import { contactInput, contactPatch } from "./contacts";
import { parseGuestCount } from "../guestCount";
import { scheduleV1 } from "./schedule";

export const guestCountInput = z.union([z.number().int().nonnegative(), z.string().trim().max(80)])
  .refine((value) => parseGuestCount(value) !== null, "Enter a guest count or range, e.g. 150, 100–150 or 150+");

export const leadStatus = z.enum(["new", "contacted", "tour_scheduled", "toured", "converted", "lost", "nurture", "no_contact", "dnc"]);
export type LeadStatus = z.infer<typeof leadStatus>;
/** Statuses a person may pick by hand (SPEC §5.1); `tour_scheduled` and `converted` come from actions. */
export const pickableLeadStatus = z.enum(["new", "contacted", "toured", "lost", "nurture", "no_contact", "dnc"]);
export type PickableLeadStatus = z.infer<typeof pickableLeadStatus>;
/** Open = still in the pipeline (dedupe and the call list look only at these). */
export const OPEN_LEAD_STATUSES = ["new", "contacted", "tour_scheduled", "toured", "nurture", "no_contact"] as const;
export const CLOSED_LEAD_STATUSES = ["lost", "converted", "dnc"] as const;

const leadFields = {
  venueId: id.nullable().optional(),
  eventTypeId: id.nullable().optional(),
  eventTypeRaw: z.string().trim().max(200).nullable().optional(),
  eventName: z.string().trim().max(200).nullable().optional(),
  eventDate: dateStr.nullable().optional(),
  eventDateFlexible: flag.optional(),
  guestCount: guestCountInput.nullable().optional(),
  budget: z.number().int().min(0).nullable().optional(),
  notes: nullableText.optional(),
  sourceId: id.nullable().optional(),
  sourceDetail: z.string().trim().max(500).nullable().optional(),
  assignedMemberId: id.nullable().optional(),
  nextActionAt: instantDateTime.nullable().optional(),
  nextActionNote: z.string().trim().max(500).nullable().optional(),
  secondaryContactId: id.nullable().optional(),
  secondaryContact: contactInput.nullable().optional(),
};

/** Manual `+ Lead` sheet. */
export const leadCreate = z.object({
  contactId: id.optional(),
  contact: contactInput.optional(),
  ...leadFields,
});
export type LeadCreate = z.infer<typeof leadCreate>;

export const leadPatch = z.object({ ...leadFields, contact: contactPatch.optional() });
export type LeadPatch = z.infer<typeof leadPatch>;

export const leadStatusChange = z.object({ status: pickableLeadStatus, lostReason: z.string().trim().max(500).optional() });
export type LeadStatusChange = z.infer<typeof leadStatusChange>;

export const leadConvert = z.object({
  venueId: id.optional(),
  eventDate: dateStr.optional(),
  packageCatalogItemId: id.optional(),
  packageQty: z.number().int().min(1).optional(),
  eventName: z.string().trim().max(200).nullable().optional(),
  guestCount: guestCountInput.nullable().optional(),
  startsAt: localDateTime.nullable().optional(),
  endsAt: localDateTime.nullable().optional(),
  schedule: scheduleV1.optional(),
}).refine(input => !input.schedule || (input.eventDate === undefined && input.startsAt === undefined && input.endsAt === undefined), { message: "Use the canonical schedule without legacy date or time fields", path: ["schedule"] });
export type LeadConvert = z.infer<typeof leadConvert>;

/** Audit persisted columns, never parse a stored row through creation defaults
 * or the free-text guestCount command transform. */
export const leadRecord = z.object({
  ...leadFields,
  // Intake combines separately bounded note/message input with provenance.
  // A persisted record is not a new manual 4,000-character command.
  notes: z.string().nullable().optional(),
  contactId: id.nullable().optional(),
  contact: z.never().optional(),
  secondaryContact: z.never().optional(),
  status: leadStatus.optional(),
  guestCount: z.number().int().nonnegative().nullable().optional(),
  guestCountMin: z.number().int().nonnegative().nullable().optional(),
  guestCountMax: z.number().int().nonnegative().nullable().optional(),
  lostReason: z.string().max(500).nullable().optional(),
  bookingId: id.nullable().optional(),
});

export const leadListQuery = z.object({
  status: csv.optional(),
  venueId: id.optional(),
  assignedMemberId: id.optional(),
  q: z.string().trim().optional(),
  sort: z.enum(["activity", "next_action", "event_date"]).default("activity"),
  limit,
  cursor: z.coerce.number().int().min(0).optional(),
});
export type LeadListQuery = z.infer<typeof leadListQuery>;
